/* ==========================================================================
   AYOVA VANILLA JAVASCRIPT CAROUSEL CONTROLLER
   Supports: Touch swipe, Mouse drag, prev/next controls, dots, responsive items
   ========================================================================== */

class AyovaCarousel {
  constructor(element, options = {}) {
    this.container = typeof element === 'string' ? document.querySelector(element) : element;
    if (!this.container) return;
    if (this.container._ayovaCarousel) return this.container._ayovaCarousel;
    this.container._ayovaCarousel = this;

    this.section = this.container.closest('section') || this.container;
    this.track = this.container.querySelector('.ayova-carousel-track');
    if (!this.track) return;

    // Filter to only actual slide elements, ignoring <style>, <script>, etc.
    const children = Array.from(this.track.children || []);
    this.slides = children.filter(child => {
      const tag = child.tagName.toLowerCase();
      return tag !== 'style' && tag !== 'script' && tag !== 'template';
    });
    if (this.slides.length === 0) return;

    const outer = this.container.closest('.ayova-carousel-outer');
    this.prevBtn = this.container.querySelector('[data-carousel-prev]') || outer?.querySelector('[data-carousel-prev]') || this.section.querySelector('[data-carousel-prev]');
    this.nextBtn = this.container.querySelector('[data-carousel-next]') || outer?.querySelector('[data-carousel-next]') || this.section.querySelector('[data-carousel-next]');
    this.dotsContainer = this.container.querySelector('[data-carousel-dots]') ||
      this.container.parentElement?.querySelector('[data-carousel-dots]') ||
      this.section?.querySelector('[data-carousel-dots]') ||
      this.container.closest('section, .shopify-section, main')?.querySelector('[data-carousel-dots]');

    this.currentIndex = 0;
    this.customItems = this.container.getAttribute('data-items-per-view') || this.section.getAttribute('data-items-per-view');
    this.gap = parseInt(options.gap || this.container.getAttribute('data-gap') || 20);
    this.autoPlay = options.autoPlay || this.container.hasAttribute('data-autoplay');
    this.autoPlayInterval = parseInt(this.container.getAttribute('data-autoplay-interval') || options.autoPlayInterval || 3500);
    this.autoPlayDirection = 1;
    this.timer = null;
    this.resumeTimer = null;

    this.init();
  }

  calculateItemsPerView() {
    const width = window.innerWidth;
    const target = this.customItems ? parseInt(this.customItems) : 4;

    if (target === 1) return 1;

    if (width < 640) {
      // 2 items per view on mobile screens for product/combo carousels
      return Math.min(2, target);
    } else if (width < 900) {
      return Math.min(2, target);
    } else if (width < 1200) {
      return Math.min(3, target);
    } else if (width < 1400) {
      return Math.min(4, target);
    }
    return target;
  }

  init() {
    this.track.style.gap = `${this.gap}px`;
    // GPU acceleration & mobile scroll hints
    this.track.style.willChange = 'transform';
    this.track.style.webkitOverflowScrolling = 'touch';
    this.container.style.touchAction = 'pan-y';
    this.updateSlideWidths();
    this.createDots();
    this.bindEvents();
    this.update();

    if (this.autoPlay) {
      this.startAutoPlay();
    }
  }

  updateSlideWidths() {
    const computedStyle = window.getComputedStyle(this.container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const availableWidth = this.container.clientWidth - paddingLeft - paddingRight;
    this.itemsPerView = this.calculateItemsPerView();

    // Use a tighter gap on small screens for multi-item (2-per-view) layouts
    const effectiveGap = (window.innerWidth < 768 && this.itemsPerView > 1) ? Math.min(10, this.gap) : this.gap;
    this.effectiveGap = effectiveGap;
    this.track.style.gap = `${effectiveGap}px`;

    if (this.itemsPerView === 1) {
      // Single slide view (hero banner): must cover exactly 100% full screen with zero buffer gap
      const slideWidth = availableWidth;
      this.slides.forEach(slide => {
        slide.style.width = `${slideWidth}px`;
        slide.style.minWidth = `${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.boxSizing = 'border-box';
      });
    } else {
      // Calculate width taking gap into account
      const totalGapsWidth = Math.max(0, (this.itemsPerView - 1) * effectiveGap);
      // 4px safety buffer to ensure subpixel rounding and card borders never get clipped by overflow:hidden
      const slideWidth = Math.max(120, Math.floor((availableWidth - totalGapsWidth - 4) / this.itemsPerView));

      this.slides.forEach(slide => {
        slide.style.width = `${slideWidth}px`;
        slide.style.minWidth = '';
        slide.style.maxWidth = '';
        slide.style.flex = `0 0 ${slideWidth}px`;
      });
    }

    this.maxIndex = Math.max(0, this.slides.length - this.itemsPerView);
  }

  createDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const totalDots = this.maxIndex + 1;
    if (totalDots <= 1) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    this.dotsContainer.style.display = 'flex';
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('div');
      dot.classList.add('ayova-carousel-dot');
      if (i === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        if (this.autoPlay) {
          this.stopAutoPlay();
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => this.startAutoPlay(), 4000);
        }
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        if (this.autoPlay) {
          this.stopAutoPlay();
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => this.startAutoPlay(), 4000);
        }
      });
    }

    // Pause on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoPlay());
    this.container.addEventListener('mouseleave', () => {
      if (this.autoPlay) this.startAutoPlay();
    });

    let resizeTimeout;
    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateSlideWidths();
        this.createDots();
        this.goTo(Math.min(this.currentIndex, this.maxIndex));
      }, 50);
    };
    window.addEventListener('resize', onResize);

    // Re-evaluate on window load (when all assets, stylesheets & scrollbars settle)
    window.addEventListener('load', () => {
      this.updateSlideWidths();
      this.update();
    });

    // Re-evaluate when web fonts finish loading
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.updateSlideWidths();
        this.update();
      });
    }

    // Use ResizeObserver for accurate sizing whenever container dimensions change
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSlideWidths();
        this.update();
      });
      this.resizeObserver.observe(this.container);
    }

    // Touch & Mouse Drag Support
    let startX = 0;
    let currentTranslate = 0;
    let prevTranslate = 0;
    let isDragging = false;

    const getPositionX = (e) => e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;

    const touchStart = (e) => {
      isDragging = true;
      startX = getPositionX(e);
      this.stopAutoPlay();
      this.track.style.transition = 'none';
    };

    const touchMove = (e) => {
      if (!isDragging) return;
      const currentX = getPositionX(e);
      const diffX = currentX - startX;
      const gap = this.effectiveGap !== undefined ? this.effectiveGap : this.gap;
      const slideWidth = this.itemsPerView === 1 ? this.container.clientWidth : (parseFloat(this.slides[0].style.width) || 300);
      const baseMove = -(slideWidth + gap) * this.currentIndex;

      // Rubber-band at edges: 25% resistance when dragging past first/last
      const atStart = this.currentIndex <= 0 && diffX > 0;
      const atEnd   = this.currentIndex >= this.maxIndex && diffX < 0;
      if (atStart || atEnd) {
        currentTranslate = baseMove + (diffX * 0.25);
      } else {
        currentTranslate = baseMove + diffX;
      }
      this.track.style.transform = `translateX(${currentTranslate}px)`;
    };

    const touchEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      this.track.style.transition = 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
      const gap = this.effectiveGap !== undefined ? this.effectiveGap : this.gap;
      const slideWidth = this.itemsPerView === 1 ? this.container.clientWidth : (parseFloat(this.slides[0].style.width) || 300);
      const baseMove = -(slideWidth + gap) * this.currentIndex;
      const movedBy = currentTranslate - baseMove;

      // Lower threshold (40px) for snappier swipe response on mobile
      if (movedBy < -40) {
        this.next(); // wraps to first after last
      } else if (movedBy > 40) {
        this.prev(); // wraps to last before first
      } else {
        this.update();
      }

      if (this.autoPlay) {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => this.startAutoPlay(), 3500);
      }
    };

    this.track.addEventListener('touchstart', touchStart, { passive: true });
    this.track.addEventListener('touchmove', touchMove, { passive: true });
    this.track.addEventListener('touchend', touchEnd, { passive: true });

    this.track.addEventListener('mousedown', touchStart);
    this.track.addEventListener('mousemove', touchMove);
    this.track.addEventListener('mouseup', touchEnd);
    this.track.addEventListener('mouseleave', () => { if (isDragging) touchEnd(); });
  }

  goTo(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.update();
  }

  prev() {
    const step = (window.innerWidth < 768 && this.itemsPerView === 2) ? 2 : 1;
    if (this.currentIndex <= 0) {
      // First slide → wrap to last (infinite loop)
      this.currentIndex = this.maxIndex;
    } else {
      this.currentIndex = Math.max(0, this.currentIndex - step);
    }
    this.update();
  }

  next() {
    const step = (window.innerWidth < 768 && this.itemsPerView === 2) ? 2 : 1;
    if (this.currentIndex >= this.maxIndex) {
      // Last slide → wrap to first (infinite loop)
      this.currentIndex = 0;
    } else {
      this.currentIndex = Math.min(this.currentIndex + step, this.maxIndex);
    }
    this.update();
  }

  update() {
    if (!this.slides[0]) return;

    // Clamp currentIndex safely
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));

    const gap = this.effectiveGap !== undefined ? this.effectiveGap : this.gap;
    const slideWidth = this.itemsPerView === 1 ? this.container.clientWidth : (parseFloat(this.slides[0].style.width) || 300);
    const moveX = (slideWidth + gap) * this.currentIndex;
    // Smooth spring easing — feels native on mobile
    this.track.style.transition = 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
    this.track.style.transform = `translateX(-${moveX}px)`;

    // Update Buttons — infinite loop: buttons always active (never disabled)
    const noScroll = this.maxIndex <= 0;

    if (this.prevBtn) {
      this.prevBtn.disabled = noScroll;
      this.prevBtn.style.opacity = noScroll ? '0.35' : '1';
      this.prevBtn.style.pointerEvents = noScroll ? 'none' : 'auto';
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = noScroll;
      this.nextBtn.style.opacity = noScroll ? '0.35' : '1';
      this.nextBtn.style.pointerEvents = noScroll ? 'none' : 'auto';
    }

    // Update Dots
    if (this.dotsContainer) {
      const dots = Array.from(this.dotsContainer.children);
      dots.forEach((dot, idx) => {
        dot.classList.toggle('active', idx === this.currentIndex);
      });
    }
  }

  startAutoPlay() {
    this.stopAutoPlay();

    this.timer = setInterval(() => {
      if (this.maxIndex <= 0) return;

      const step = (window.innerWidth < 768 && this.itemsPerView === 2) ? 2 : 1;

      if (this.currentIndex < this.maxIndex) {
        const nextIndex = Math.min(this.currentIndex + step, this.maxIndex);
        this.goTo(nextIndex);
      } else {
        // After last moved/finished, slider restarts back from start (index 0)
        this.goTo(0);
      }
    }, this.autoPlayInterval);
  }

  stopAutoPlay() {
    if (this.timer) clearInterval(this.timer);
  }
}

// Auto Initialize all carousels with [data-ayova-carousel] attribute
function initAllAyovaCarousels() {
  document.querySelectorAll('[data-ayova-carousel]').forEach(el => {
    if (!el._ayovaCarousel) {
      el._ayovaCarousel = new AyovaCarousel(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllAyovaCarousels);
} else {
  initAllAyovaCarousels();
}

window.AyovaCarousel = AyovaCarousel;